import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../BreadCrumb";
import { MdOutlineMyLocation } from "react-icons/md";
import SummaryApi from "../../common/SummaryApi";
import apiRequest from "../../common/apiRequest";
import { toast } from "react-hot-toast";

const inputClass = `
  w-full px-3 py-2.5 text-sm
  rounded-lg
  bg-[#F9FAFB]
  border border-[#A2A1A833]
  bg-transparent
  outline-none
`;

const AddressDetailForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    country: "",
    pincode: "",
    flat: "",
    area: "",
    landmark: "",
    city: "",
    state: "",
    type: "office",
    isDefault: true,
  });

  useEffect(() => {
    if (!isEdit) return;

    const loadAddress = async () => {
      try {
        const res = await apiRequest({
          ...SummaryApi.getAddressById,
          url: SummaryApi.getAddressById.url(id),
        });

        // IMPORTANT: map only form fields
        setForm({
          name: res.address.name || "",
          phone: res.address.phone || "",
          country: res.address.country || "",
          pincode: res.address.pincode || "",
          flat: res.address.flat || "",
          area: res.address.area || "",
          landmark: res.address.landmark || "",
          city: res.address.city || "",
          state: res.address.state || "",
          type: res.address.type || "office",
          isDefault: res.address.isDefault || false,
        });
      } catch (err) {
        toast.error("Failed to load address");
        console.error(err);
      }
    };

    loadAddress();
  }, [id, isEdit]);

  /*  Detect Location */
  const detectLocation = () => {
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;

      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=en`,
        {
          headers: {
            "User-Agent": "YourAppName/1.0"
          }
        }
      );

      const data = await res.json();
      const addr = data.address || {};

      setForm(prev => ({
        ...prev,
        country: addr.country || "",
        state: addr.state || "",
        city: addr.city || addr.town || "",
        area: addr.neighbourhood || "",
        landmark:  addr.suburb || "",
        pincode: addr.postcode || ""
      }));
    });
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      let res;

      if (isEdit) {
        res = await apiRequest(
          {
            ...SummaryApi.updateAddress,
            url: SummaryApi.updateAddress.url(id),
          },
          form
        );
        toast.success(res.message || "Address updated successfully");
      } else {
        res = await apiRequest(
          SummaryApi.addAddress,
          form
        );
        toast.success(res.message || "Address saved successfully");
      }

      navigate("/my-account/address");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Something went wrong";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    // <div className="bg-[#F7F8FA] min-h-screen px-4 py-6">
      <div className="mx-auto space-y-6">

        {/* BREADCRUMB */}
        <Breadcrumb
          items={[
            { label: "Home", path: "/" },
            { label: "My Account", path: "/my-account" },
            { label: "Address", path: "/my-account/address" },
            { label: isEdit ? "Edit" : "New" },
          ]}
        />

        {/* DETECT LOCATION */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2 items-start">
            <MdOutlineMyLocation size={18} className="text-[#007BFF] mt-0.5" />
            <div>
              <p className="text-sm text-[#007BFF] font-medium">
                Detect current location
              </p>
              <p className="text-xs text-[#8080808C] font-medium">
                Enable your current location
              </p>
            </div>
          </div>

          <button
            onClick={detectLocation}
            className="border border-[#8080808C] px-4 py-1.5 rounded-lg text-sm text-[#007BFF]"
          >
            Enable
          </button>
        </div>

        {/* FORM */}
        <div className="bg-[#c7c7ca33] rounded-md space-y-3 px-3 py-6">

          <input placeholder="Name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputClass}
          />

          <input placeholder="Mobile number" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className={inputClass}
          />

          <input placeholder="Country/Region" value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value })}
            className={inputClass}
          />

          <input placeholder="Pincode" value={form.pincode}
            onChange={(e) => setForm({ ...form, pincode: e.target.value })}
            className={inputClass}
          />

          <input
            placeholder="Flat, House no, Building, Company, Apartment"
            value={form.flat}
            onChange={(e) => setForm({ ...form, flat: e.target.value })}
            className={inputClass}
          />

          <input
            placeholder="Area, Street, Village"
            value={form.area}
            onChange={(e) => setForm({ ...form, area: e.target.value })}
            className={inputClass}
          />

          <input
            placeholder="Landmark"
            value={form.landmark}
            onChange={(e) => setForm({ ...form, landmark: e.target.value })}
            className={inputClass}
          />

          {/* CITY + STATE */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              placeholder="Town/City"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className={inputClass}
            />
            <input
              placeholder="State"
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
              className={inputClass}
            />
          </div>

          {/* ADDRESS TYPE */}
          <div>
            <p className="text-sm font-medium mb-2">Address type</p>
            <input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputClass} />
          </div>

          {/* DEFAULT */}
          <label className="flex items-center gap-2 text-xs text-gray-500">
            <input
              type="checkbox"
              checked={form.isDefault}
              onChange={() =>
                setForm({ ...form, isDefault: !form.isDefault })
              }
            />
            Make this my default address
          </label>

          {/* SAVE BUTTON */}
          <div className="pt-4">
            <button
              onClick={handleSave}
              disabled={loading}
              className="w-full md:w-auto bg-[#2563EB] text-white px-6 py-2.5 rounded-lg text-sm font-medium"
            >
              {loading ? "Saving..." : "Save Address"}
            </button>
          </div>

        </div>
      </div>
    // </div>
  );
};

export default AddressDetailForm;
